import React, { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Link2,
  X,
  Loader2,
} from 'lucide-react';
import { theme } from '../../styles/theme';
import metricsApi, {
  SubDimension,
  CreateSubDimensionRequest,
  UpdateSubDimensionRequest,
  Cluster,
  Metric,
} from '../../api/metricsApi';

interface SubDimensionFormData {
  name: string;
  description: string;
  clusterId: string;
}

export const SubDimensionManagement: React.FC = () => {
  const [subDimensions, setSubDimensions] = useState<SubDimension[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [editingSubDimension, setEditingSubDimension] = useState<SubDimension | null>(null);
  const [linkingSubDimension, setLinkingSubDimension] = useState<SubDimension | null>(null);
  const [formData, setFormData] = useState<SubDimensionFormData>({
    name: '',
    description: '',
    clusterId: '',
  });
  const [selectedClusterId, setSelectedClusterId] = useState<string>('');
  const [formErrors, setFormErrors] = useState<Partial<SubDimensionFormData>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [subDimsData, clustersData, metricsData] = await Promise.all([
        metricsApi.getSubDimensions(),
        metricsApi.getClusters(),
        metricsApi.getMetrics(),
      ]);
      setSubDimensions(subDimsData);
      setClusters(clustersData);
      setMetrics(metricsData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (subDimension?: SubDimension) => {
    if (subDimension) {
      setEditingSubDimension(subDimension);
      setFormData({
        name: subDimension.name,
        description: subDimension.description || '',
        clusterId: subDimension.clusterId?.toString() || '',
      });
    } else {
      setEditingSubDimension(null);
      setFormData({
        name: '',
        description: '',
        clusterId: '',
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSubDimension(null);
    setFormData({
      name: '',
      description: '',
      clusterId: '',
    });
    setFormErrors({});
  };

  const handleOpenLinkModal = (subDimension: SubDimension) => {
    setLinkingSubDimension(subDimension);
    setSelectedClusterId(subDimension.clusterId?.toString() || '');
    setShowLinkModal(true);
  };

  const handleCloseLinkModal = () => {
    setShowLinkModal(false);
    setLinkingSubDimension(null);
    setSelectedClusterId('');
  };

  const validateForm = () => {
    const errors: Partial<SubDimensionFormData> = {};

    if (!formData.name.trim()) {
      errors.name = 'Sub-dimension name is required';
    }

    if (!formData.clusterId) {
      errors.clusterId = 'Please select a cluster';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError(null);

    try {
      if (editingSubDimension) {
        const updateData: UpdateSubDimensionRequest = {
          name: formData.name,
          description: formData.description || undefined,
        };
        await metricsApi.updateSubDimension(editingSubDimension.id, updateData);
        
        // If cluster changed, update the link
        if (formData.clusterId !== editingSubDimension.clusterId?.toString()) {
          await metricsApi.linkSubDimensionToCluster(
            editingSubDimension.id,
            parseInt(formData.clusterId)
          );
        }
      } else {
        const createData: CreateSubDimensionRequest = {
          clusterId: parseInt(formData.clusterId),
          name: formData.name,
          description: formData.description || undefined,
        };
        await metricsApi.createSubDimension(createData);
      }
      
      await fetchData();
      handleCloseModal();
    } catch (err: any) {
      console.error('Failed to save sub-dimension:', err);
      setError(err.message || 'Failed to save sub-dimension. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLinkUpdate = async () => {
    if (!linkingSubDimension || !selectedClusterId) return;

    setSaving(true);
    setError(null);

    try {
      await metricsApi.linkSubDimensionToCluster(
        linkingSubDimension.id,
        parseInt(selectedClusterId)
      );
      await fetchData();
      handleCloseLinkModal();
    } catch (err: any) {
      console.error('Failed to update link:', err);
      setError(err.message || 'Failed to update cluster link. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (subDimensionId: number, clusterId: number) => {
    if (window.confirm('Are you sure you want to unlink this sub-dimension from its cluster?')) {
      try {
        setError(null);
        await metricsApi.unlinkSubDimensionFromCluster(subDimensionId, clusterId);
        await fetchData();
      } catch (err: any) {
        console.error('Failed to unlink:', err);
        setError(err.message || 'Failed to unlink sub-dimension from cluster.');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this sub-dimension? This action cannot be undone.')) {
      try {
        setError(null);
        await metricsApi.deleteSubDimension(id);
        await fetchData();
      } catch (err: any) {
        console.error('Failed to delete sub-dimension:', err);
        setError(err.message || 'Failed to delete sub-dimension. It may have associated metrics.');
      }
    }
  };

  const getClusterName = (clusterId: number | undefined) => {
    if (!clusterId) return '-';
    const cluster = clusters.find(c => c.id === clusterId);
    return cluster ? cluster.name : '-';
  };

  const getMetricsCount = (subDimensionId: number) => {
    return metrics.filter(m => m.subDimension?.id === subDimensionId).length;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2
            className="text-lg font-semibold"
            style={{
              color: theme.colors.text.primary,
              fontFamily: theme.fonts.primary,
            }}
          >
            Sub-dimension Management
          </h2>
          <p
            className="text-sm mt-1"
            style={{ 
              color: theme.colors.text.secondary,
              fontFamily: theme.fonts.primary,
            }}
          >
            Manage sub-dimensions and their cluster associations
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 hover:opacity-90"
          style={{
            backgroundColor: theme.colors.primary.main,
            color: 'white',
            fontFamily: theme.fonts.primary,
          }}
        >
          <Plus className="w-4 h-4" />
          <span>New Sub-dimension</span>
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div 
          className="p-4 rounded-lg border"
          style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            borderColor: theme.colors.status.error,
            color: theme.colors.status.error,
          }}
        >
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-lg shadow-sm border" style={{ borderColor: theme.colors.border.light }}>
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: 'rgba(250, 175, 144, 0.08)' }}>
              <th className="text-left px-6 py-4" style={{ 
                color: theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em',
                fontFamily: theme.fonts.primary,
              }}>Name</th>
              <th className="text-left px-6 py-4" style={{ 
                color: theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em',
                fontFamily: theme.fonts.primary,
              }}>Description</th>
              <th className="text-left px-6 py-4" style={{ 
                color: theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em',
                fontFamily: theme.fonts.primary,
              }}>Linked Cluster</th>
              <th className="text-center px-6 py-4" style={{ 
                color: theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em',
                fontFamily: theme.fonts.primary,
              }}>Metrics</th>
              <th className="text-center px-6 py-4" style={{ 
                color: theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em',
                fontFamily: theme.fonts.primary,
              }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center">
                  <div className="flex justify-center items-center min-h-[200px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2" 
                         style={{ borderColor: theme.colors.primary.main }}></div>
                  </div>
                </td>
              </tr>
            ) : subDimensions.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center" style={{ 
                  color: theme.colors.text.secondary,
                  fontFamily: theme.fonts.primary,
                  fontSize: '14px',
                }}>
                  No sub-dimensions found. Add your first sub-dimension to get started.
                </td>
              </tr>
            ) : (
              subDimensions.map((subDim) => (
                <tr 
                  key={subDim.id}
                  className="hover:bg-opacity-30 transition-colors"
                  style={{
                    borderBottom: `1px solid ${theme.colors.border.light}`,
                  }}
                >
                  <td className="px-6 py-4" style={{ 
                    color: theme.colors.text.primary,
                    fontFamily: theme.fonts.primary,
                    fontSize: '14px',
                    fontWeight: 500,
                  }}>
                    {subDim.name}
                  </td>
                  <td className="px-6 py-4" style={{ 
                    color: theme.colors.text.secondary,
                    fontFamily: theme.fonts.primary,
                    fontSize: '13px',
                  }}>
                    {subDim.description || '-'}
                  </td>
                  <td className="px-6 py-4">
                    {subDim.clusterId ? (
                      <span 
                        className="px-2.5 py-1 rounded-md text-xs font-semibold"
                        style={{
                          backgroundColor: theme.colors.primary.main + '30',
                          color: theme.colors.primary.dark,
                          border: `1px solid ${theme.colors.primary.main}50`,
                          fontFamily: theme.fonts.primary,
                        }}
                      >
                        {getClusterName(subDim.clusterId)}
                      </span>
                    ) : (
                      <span style={{ 
                        color: theme.colors.text.secondary,
                        fontFamily: theme.fonts.primary,
                        fontSize: '13px',
                      }}>
                        Not linked
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center" style={{ 
                    color: theme.colors.text.primary,
                    fontFamily: theme.fonts.primary,
                    fontSize: '14px',
                    fontWeight: 600,
                  }}>
                    {getMetricsCount(subDim.id)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center items-center gap-2">
                      <button
                        onClick={() => handleOpenModal(subDim)}
                        className="p-1.5 rounded-md hover:bg-opacity-10 transition-colors"
                        title="Edit sub-dimension"
                        style={{
                          color: theme.colors.text.secondary,
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleOpenLinkModal(subDim)}
                        className="p-1.5 rounded-md hover:bg-opacity-10 transition-colors"
                        title="Link/Unlink Cluster"
                        style={{
                          color: theme.colors.secondary.main,
                        }}
                      >
                        <Link2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(subDim.id)}
                        className="p-1.5 rounded-md hover:bg-opacity-10 transition-colors"
                        title="Delete sub-dimension"
                        style={{
                          color: theme.colors.status.error,
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
                {editingSubDimension ? 'Edit Sub-dimension' : 'Add New Sub-dimension'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="px-6 py-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
                  Sub-dimension Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter sub-dimension name"
                  className={`w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] ${
                    formErrors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  style={{ fontFamily: '"Tenor Sans", sans-serif' }}
                />
                {formErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter sub-dimension description (optional)"
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] border-gray-300"
                  style={{ fontFamily: '"Tenor Sans", sans-serif' }}
                />
              </div>

              {/* Cluster Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
                  Cluster *
                </label>
                <select
                  value={formData.clusterId}
                  onChange={(e) => setFormData({ ...formData, clusterId: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] ${
                    formErrors.clusterId ? 'border-red-500' : 'border-gray-300'
                  }`}
                  style={{ fontFamily: '"Tenor Sans", sans-serif' }}
                >
                  <option value="">Select a cluster</option>
                  {clusters.map((cluster) => (
                    <option key={cluster.id} value={cluster.id.toString()}>
                      {cluster.name}
                    </option>
                  ))}
                </select>
                {formErrors.clusterId && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.clusterId}</p>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                style={{ fontFamily: '"Tenor Sans", sans-serif' }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-4 py-2 bg-[#FAAF90] text-white rounded-lg hover:bg-[#E59873] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ fontFamily: '"Tenor Sans", sans-serif' }}
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingSubDimension ? 'Update' : 'Create'} Sub-dimension
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link/Unlink Modal */}
      {showLinkModal && linkingSubDimension && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
                Link Sub-dimension to Cluster
              </h2>
              <button
                onClick={handleCloseLinkModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="px-6 py-4 space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-4" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
                  Select a cluster to link "{linkingSubDimension.name}" to:
                </p>
                <select
                  value={selectedClusterId}
                  onChange={(e) => setSelectedClusterId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] border-gray-300"
                  style={{ fontFamily: '"Tenor Sans", sans-serif' }}
                >
                  <option value="">Select a cluster</option>
                  {clusters.map((cluster) => (
                    <option key={cluster.id} value={cluster.id.toString()}>
                      {cluster.name}
                    </option>
                  ))}
                </select>
              </div>

              {linkingSubDimension.clusterId && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
                    Currently linked to: <strong>{getClusterName(linkingSubDimension.clusterId)}</strong>
                  </p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              <div>
                {linkingSubDimension.clusterId && (
                  <button
                    onClick={() => {
                      handleUnlink(linkingSubDimension.id, linkingSubDimension.clusterId!);
                      handleCloseLinkModal();
                    }}
                    className="px-4 py-2 text-red-600 hover:text-red-800"
                    style={{ fontFamily: '"Tenor Sans", sans-serif' }}
                    disabled={saving}
                  >
                    Unlink Current
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCloseLinkModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  style={{ fontFamily: '"Tenor Sans", sans-serif' }}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleLinkUpdate}
                  disabled={saving || !selectedClusterId}
                  className="px-4 py-2 bg-[#FAAF90] text-white rounded-lg hover:bg-[#E59873] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  style={{ fontFamily: '"Tenor Sans", sans-serif' }}
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Update Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};